import { useNavigate } from "react-router";
import React, { useState, useEffect } from "react";
import { App, Button, Form as AntdForm, Input, Typography } from "antd";
import { connect } from "react-redux";
import {
  forgotPassword as forgotPasswordAction,
  forgotPasswordErrorClear as forgotPasswordErrorClearAction,
  ForgotPasswordResponse,
  ForgotPasswordAction,
} from "@triare/auth-redux/dist/saga/auth/forgotPassword";
import { Error } from "@triare/auth-redux/dist/saga/common";
import { Action } from "@triare/auth-redux";
import {
  validationSignInEmailAndPassword,
  createRulesForAntd,
} from "../../../../utils/validations";
import SendSuccess from "./SendSuccess";
import { getMessageInError } from "../../../../hooks/fetch";
import Wrapper from "../Wrapper";
import Submit from "../../../Common/Form/Button/Submit";
import { RootState } from "../../../../store";
import { moduleName } from "../../../../store/auth";

import styles from "./index.module.scss";
import { useTranslation } from "react-i18next";

const { Title, Paragraph } = Typography;
const validationRules = createRulesForAntd(validationSignInEmailAndPassword);

interface ForgotPasswordProps {
  loading: boolean;
  response: ForgotPasswordResponse | null;
  error: Error;
  forgotPassword: (payload: ForgotPasswordAction) => Action;
  forgotPasswordErrorClear: () => Action;
}

function ForgotPassword({
  forgotPassword,
  forgotPasswordErrorClear,
  loading,
  response,
  error,
}: ForgotPasswordProps) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [sendSuccess, setSendSuccess] = useState(false);

  // Reported through the platform's message toast, like every other failed
  // request in the app, instead of an alert inside the form. Cleared once
  // shown, the way useMessageError does it, so a stale error cannot surface
  // when the screen is opened again.
  useEffect(() => {
    if (error) {
      message.error(getMessageInError(error));
      forgotPasswordErrorClear();
    }
  }, [error]);

  const onFinish = ({ email }: { email: string }) => {
    forgotPassword({ email });
  };

  useEffect(() => {
    if (response) {
      setSendSuccess(true);
    }
  }, [response]);

  return (
    <Wrapper>
      <AntdForm
        className={styles.form}
        layout="vertical"
        scrollToFirstError
        onFinish={onFinish}
      >
        {sendSuccess ? (
          <SendSuccess />
        ) : (
          <>
            <Title level={3} className={styles.text}>
              {t("pages.forgotPassword.title")}
            </Title>
            <Paragraph className={styles.secondary}>
              {t("pages.forgotPassword.description")}
            </Paragraph>

            <AntdForm.Item
              name="email"
              required
              label={t("email")}
              rules={[validationRules]}
            >
              <Input size="large" placeholder={t("email")} />
            </AntdForm.Item>

            <Submit loading={loading} size="large" block>
              {t("resetPassword")}
            </Submit>

            <AntdForm.Item className={styles.backLink}>
              <Button type="link" onClick={() => navigate("/sign-in")}>
                {t("common.backToLogin")}
              </Button>
            </AntdForm.Item>

            {/* for test */}
            {}
            <button onClick={() => setSendSuccess(true)}>
              test button <br /> see what happens after a successful request
            </button>
          </>
        )}
      </AntdForm>
    </Wrapper>
  );
}

export default connect(
  (state: RootState) => ({
    loading: state[moduleName].forgotPassword.loading,
    response: state[moduleName].forgotPassword.response,
    error: state[moduleName].forgotPassword.error,
  }),
  {
    forgotPassword: forgotPasswordAction,
    forgotPasswordErrorClear: forgotPasswordErrorClearAction,
  },
)(ForgotPassword);
